import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Pencil, Trash2, SlidersHorizontal, RefreshCw, AlertTriangle } from "lucide-react";
import { useSettingsProfile } from "@/api/queries/settingsProfiles";
import {
  useMutateProfileLayer,
  useDeleteSettingsProfile,
  type ProfileLayer,
} from "@/api/mutations/settingsProfiles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { ApiError } from "@/api/client";
import { giteaSeedUrl } from "@/config/env";
import { ConfigSection } from "@/components/agent-config/ConfigSection";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UpdateSettingsProfileDialog } from "@/components/settings-profile-actions/UpdateSettingsProfileDialog";
import { BlueprintSection } from "@/components/settings-profile-actions/BlueprintSection";

export function SettingsProfileDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useSettingsProfile(id);
  const mutateLayer = useMutateProfileLayer();
  const deleteMutation = useDeleteSettingsProfile();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `Profil "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  if (!id) return null;

  function makeHandler(layer: ProfileLayer, label: string, mode: "merge" | "replace") {
    return (payload: Record<string, unknown>) => {
      mutateLayer.mutate(
        { id: id!, layer, payload, mode },
        {
          onSuccess: () => toast.success(`${label} ${mode === "replace" ? "ersetzt" : "aktualisiert"}`),
          onError: (err) => toast.error(`${label} fehlgeschlagen: ${(err as Error).message}`),
        },
      );
    };
  }

  function handleDelete() {
    setDeleteError(null);
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        toast.success("Profil gelöscht");
        navigate("/settings-profiles");
      },
      onError: (err) => {
        const msg = (err as Error).message;
        setDeleteError(msg);
        toast.error(`Löschen fehlgeschlagen: ${msg}`);
      },
    });
  }

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Profil…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/settings-profiles" label="Settings-Profile" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/settings-profiles"
        backLabel="Settings-Profile"
        icon={SlidersHorizontal}
        title={data.name}
        badge={id === "standard" ? <Badge variant="enrolled">Default</Badge> : undefined}
        sub={data.description || "3-Layer Config-Template (managed / owner / mcpServers)."}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="sp-edit-open">
              <Pencil className="h-4 w-4" />Bearbeiten
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="sp-delete-open">
              <Trash2 className="h-4 w-4" />Löschen
            </Button>
          </div>
        }
      />

      {/* Workspace-Seed (Gitea) */}
      <SectionCard testId="sp-seed">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-text3">Workspace-Seed (Gitea)</div>
        {data.seedRepo ? (
          <a
            href={giteaSeedUrl(data.seedRepo, data.seedBranch)}
            target="_blank"
            rel="noreferrer"
            className="mono mt-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-accent-text hover:underline"
            data-testid="sp-seed-link"
          >
            {data.seedRepo}<span className="text-text3">@{data.seedBranch ?? "(default)"}</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <div className="mt-1.5 text-[13px] text-text3">
            Kein Seed-Repo — Agents mit diesem Profil werden ohne Workspace-Repo erstellt
            (nachträglich setzbar). Über „Bearbeiten" ein Repo wählen.
          </div>
        )}
      </SectionCard>

      {/* #281: Agenten-Ausstattung (Blaupause) — Snapshot beim Agent-Create. */}
      <BlueprintSection profile={data} />

      {/* #215 (mcp-Umwidmung): nur noch settings (managed + owner). MCP läuft über das
          Workspace-Repo (/workspace/.mcp.json), nicht mehr über die DB-Config. */}
      <div className="grid gap-[18px] xl:grid-cols-2">
        <ConfigSection
          title="Managed"
          modeLabel="admin"
          value={data.config.managed}
          isPending={mutateLayer.isPending}
          onSave={makeHandler("managed", "Managed", "merge")}
          onReplace={makeHandler("managed", "Managed", "replace")}
        />
        <ConfigSection
          title="Owner"
          modeLabel="owner"
          value={data.config.owner}
          isPending={mutateLayer.isPending}
          onSave={makeHandler("owner", "Owner", "merge")}
          onReplace={makeHandler("owner", "Owner", "replace")}
        />
      </div>

      {editOpen && <UpdateSettingsProfileDialog profile={data} open={editOpen} onOpenChange={setEditOpen} />}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Profil löschen"
        description={
          <>
            Das Profil <strong>{data.name}</strong> wird gelöscht. Bereits erstellte
            Agents behalten ihre Config (Snapshot). Fortfahren?
          </>
        }
        confirmText="Löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        onConfirm={handleDelete}
      />
    </div>
  );
}
